import type {
  BillingClient,
  BusinessInfo,
  ClientInvoice,
  InvoicePaymentStatus,
  PurchasingInvoiceDefaults,
} from '../types';
import { computePoTotals, gstModeLabel } from './nzGst';

export interface ClientInvoiceTemplateInput {
  invoice: ClientInvoice;
  client: BillingClient;
  businessInfo: BusinessInfo;
  defaults?: PurchasingInvoiceDefaults;
}

export interface ClientInvoiceLineView {
  lineNumber: number;
  description: string;
  quantity: number;
  unit?: string;
  rate: number;
  gst: number;
  amount: number;
}

export interface ClientInvoiceViewModel {
  businessName: string;
  businessAddress: string;
  businessGstNumber: string;
  businessPhone?: string;
  businessEmail?: string;
  logoUrl?: string;
  /** Embedded logo for PDF capture when remote CORS blocks canvas. */
  logoDataUrl?: string;
  invoiceNumber: string;
  invoiceDate: string;
  dueDate?: string;
  periodLabel?: string;
  paymentTerms?: string;
  clientName: string;
  clientAddress?: string;
  clientGstNumber?: string;
  lines: ClientInvoiceLineView[];
  subtotalExGst: number;
  gstAmount: number;
  totalIncGst: number;
  balanceDue: number;
  paymentStatus: InvoicePaymentStatus;
  bankAccountName?: string;
  bankAccountNumber?: string;
  notes?: string;
  gstModeLabel: string;
}

export interface InvoiceLogoAsset {
  dataUrl: string;
  aspectRatio: number;
  format: 'PNG' | 'JPEG';
}

export function formatInvoiceDate(ms?: number): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatInvoiceMoney(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

/** Plain amount for invoice tables (no currency symbol, thousands separators). */
export function formatInvoiceAmount(amount: number): string {
  return amount.toLocaleString('en-NZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatInvoiceQty(qty: number): string {
  return qty.toLocaleString('en-NZ', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatInvoiceNzd(amount: number): string {
  return `NZD${formatInvoiceAmount(amount)}`;
}

export const INVOICE_GST_PERCENT_LABEL = '15.00%';

function resolveInvoiceLogoUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://') || trimmed.startsWith('data:')) {
    return trimmed;
  }
  if (typeof window !== 'undefined' && trimmed.startsWith('/')) {
    return `${window.location.origin}${trimmed}`;
  }
  return trimmed;
}

function parseFirebaseStoragePath(url: string): string | null {
  try {
    const parsed = new URL(url);
    if (!parsed.hostname.includes('firebasestorage.googleapis.com')) return null;
    const pathMatch = parsed.pathname.match(/\/o\/(.+)$/);
    if (!pathMatch?.[1]) return null;
    return decodeURIComponent(pathMatch[1]);
  } catch {
    return null;
  }
}

async function blobToLogoAsset(blob: Blob): Promise<InvoiceLogoAsset | null> {
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('Logo decode failed'));
      el.src = objectUrl;
    });
    if (!img.naturalWidth || !img.naturalHeight) return null;
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0);
    const format: InvoiceLogoAsset['format'] =
      blob.type.includes('jpeg') || blob.type.includes('jpg') ? 'JPEG' : 'PNG';
    const dataUrl =
      format === 'JPEG' ? canvas.toDataURL('image/jpeg', 0.92) : canvas.toDataURL('image/png');
    return {
      dataUrl,
      aspectRatio: img.naturalWidth / img.naturalHeight,
      format,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

function isLocalDevHost(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1';
}

/** Browser-safe logo URL (uses dev proxy on localhost for cross-origin assets). */
export function getInvoiceLogoDisplayUrl(url?: string): string | undefined {
  if (!url?.trim()) return undefined;
  return getInvoiceLogoFetchUrl(url.trim());
}

export function getInvoiceLogoFetchUrl(url: string): string {
  const resolved = resolveInvoiceLogoUrl(url);
  if (!isLocalDevHost()) return resolved;
  try {
    const target = new URL(resolved);
    if (target.origin === window.location.origin) return resolved;
    return `/dev-image-proxy?url=${encodeURIComponent(resolved)}`;
  } catch {
    return resolved;
  }
}

async function loadLogoBlob(url: string): Promise<Blob | null> {
  const resolved = resolveInvoiceLogoUrl(url);

  if (resolved.startsWith('data:')) {
    const res = await fetch(resolved);
    return res.ok ? res.blob() : null;
  }

  // Firebase SDK download avoids browser CORS (required for PDF/html2canvas on pos.kingspark.co.nz).
  const storagePath = parseFirebaseStoragePath(resolved);
  if (storagePath) {
    try {
      const { getBytes, ref } = await import('firebase/storage');
      const { getFirebaseStorage } = await import('./firebase');
      const bytes = await getBytes(ref(getFirebaseStorage(), storagePath));
      const lower = storagePath.toLowerCase();
      const mime = lower.endsWith('.png')
        ? 'image/png'
        : lower.endsWith('.jpg') || lower.endsWith('.jpeg')
          ? 'image/jpeg'
          : 'application/octet-stream';
      return new Blob([bytes], { type: mime });
    } catch {
      // fall through to fetch / dev proxy
    }
  }

  try {
    const res = await fetch(getInvoiceLogoFetchUrl(url));
    if (res.ok) return res.blob();
  } catch {
    // ignore
  }

  return null;
}

export async function loadInvoiceLogo(url?: string): Promise<InvoiceLogoAsset | null> {
  const trimmed = url?.trim();
  if (!trimmed) return null;

  const blob = await loadLogoBlob(trimmed);
  if (blob) {
    const fromBlob = await blobToLogoAsset(blob);
    if (fromBlob) return fromBlob;
  }

  return null;
}

export async function enrichClientInvoiceViewModelForPrint(
  vm: ClientInvoiceViewModel,
): Promise<ClientInvoiceViewModel> {
  if (vm.logoDataUrl || !vm.logoUrl) return vm;
  const logo = await loadInvoiceLogo(vm.logoUrl);
  if (!logo) return vm;
  return { ...vm, logoDataUrl: logo.dataUrl };
}

async function waitForImages(root: HTMLElement): Promise<void> {
  const images = Array.from(root.querySelectorAll('img'));
  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth > 0) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  );
}

export async function waitForInvoiceDocumentPaint(root: HTMLElement): Promise<void> {
  await waitForImages(root);
  await new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

export function buildClientInvoiceViewModel(
  input: ClientInvoiceTemplateInput,
): ClientInvoiceViewModel {
  const { invoice, client, businessInfo, defaults } = input;
  const gstMode = invoice.gstMode ?? 'inclusive';
  const poItems = invoice.items.map((line) => ({
    lineId: line.lineId,
    productName: line.description,
    quantity: line.quantity,
    costPerUnit: line.unitPrice,
    unit: line.unit as 'units',
  }));
  const totals = computePoTotals(poItems, gstMode);

  const lines: ClientInvoiceLineView[] = invoice.items.map((line, index) => {
    const breakdown = totals.lineBreakdown.find((b) => b.lineId === line.lineId);
    return {
      lineNumber: index + 1,
      description: line.description,
      quantity: line.quantity,
      unit: line.unit,
      rate: line.unitPrice,
      gst: breakdown?.gst ?? 0,
      amount: breakdown?.incGst ?? line.quantity * line.unitPrice,
    };
  });

  const periodLabel =
    invoice.periodStart && invoice.periodEnd
      ? `${formatInvoiceDate(invoice.periodStart)} – ${formatInvoiceDate(invoice.periodEnd)}`
      : undefined;

  const balanceDue = invoice.paymentStatus === 'paid' ? 0 : totals.totalIncGst;

  return {
    businessName: businessInfo.businessName?.trim() || 'Business',
    businessAddress: businessInfo.address?.trim() || '',
    businessGstNumber: businessInfo.gstNumber?.trim() || '',
    businessPhone: businessInfo.phone?.trim(),
    businessEmail: businessInfo.email?.trim(),
    logoUrl: defaults?.invoiceLogoUrl?.trim() || businessInfo.emailLogoUrl?.trim(),
    logoDataUrl: defaults?.invoiceLogoDataUrl?.trim(),
    invoiceNumber: invoice.invoiceNumber,
    invoiceDate: formatInvoiceDate(invoice.invoiceDate),
    dueDate: invoice.dueDate ? formatInvoiceDate(invoice.dueDate) : undefined,
    periodLabel,
    paymentTerms:
      client.paymentTerms?.trim() ||
      defaults?.defaultPaymentTerms?.trim() ||
      'Custom',
    clientName: client.name,
    clientAddress: client.address?.trim(),
    clientGstNumber: client.gstNumber?.trim(),
    lines,
    subtotalExGst: totals.subtotalExGst,
    gstAmount: totals.gstAmount,
    totalIncGst: totals.totalIncGst,
    balanceDue,
    paymentStatus: invoice.paymentStatus,
    bankAccountName: defaults?.bankAccountName?.trim(),
    bankAccountNumber: defaults?.bankAccountNumber?.trim(),
    notes: invoice.notes?.trim(),
    gstModeLabel: gstModeLabel(gstMode),
  };
}
